import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import './Pricing.css';

const Pricing = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [visibleElements, setVisibleElements] = useState([]);
  const [beds, setBeds] = useState(30);
  const [opdVolume, setOpdVolume] = useState(1200);
  const sectionRef = useRef(null);

  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      description: 'Perfect for small clinics and solo practitioner setups — full EMR, OPD, and basic billing.',
      icon: '🏥',
      monthlyPrice: 4999,
      yearlyPrice: 39999,
      savings: '33%',
      features: [
        'Up to 25 beds',
        'OPD & Patient Management',
        'Appointment Scheduling',
        'Basic Billing & GST Invoices',
        'Email & WhatsApp Support',
      ],
      limits: [
        '1 location',
        '3 users',
        '10 GB storage'
      ],
      popular: false,
      buttonText: 'Start Free Trial'
    },
    {
      id: 'professional',
      name: 'Professional',
      description: 'Ideal for multi-specialty hospitals with full clinical, billing, and NABH/CGHS compliance needs.',
      icon: '🏥',
      monthlyPrice: 14999,
      yearlyPrice: 119999,
      savings: '33%',
      features: [
        'Up to 100 beds',
        'OPD + IPD Management',
        'NABH & CGHS Billing',
        'TPA & Insurance Claims',
        'Advanced Analytics & Reports',
        'Priority 24/7 Support',
        'API Access',
        'Custom Integrations'
      ],
      limits: [
        'Up to 5 locations',
        '25 users',
        '100 GB storage'
      ],
      popular: true,
      buttonText: 'Start Free Trial'
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      description: 'Full-scale solution for large hospital chains and health networks across India.',
      icon: '🏥',
      monthlyPrice: 34999,
      yearlyPrice: 279999,
      savings: '33%',
      features: [
        'Unlimited beds',
        'Full Platform Access',
        'Ayushman Bharat (AB-PMJAY)',
        'Multi-location Support',
        'Custom NABH Workflows',
        'Dedicated Account Manager',
        'On-site Training & Onboarding',
        'SLA-backed 99.9% Uptime'
      ],
      limits: [
        'Unlimited locations',
        'Unlimited users',
        'Unlimited storage'
      ],
      popular: false,
      buttonText: 'Contact Sales'
    }
  ];

  const calculateROI = () => {
    let tier = 'Starter';
    let baseCost = 4999;
    
    if (beds > 25 && beds <= 100) {
      tier = 'Professional';
      baseCost = 14999;
    } else if (beds > 100) {
      tier = 'Enterprise';
      baseCost = 34999;
    }

    // 1. Bed coordination administrative hours saved (2 hours per bed per month * ₹250 wage rate)
    const bedSavings = beds * 2 * 250;
    // 2. Preventing Billing/Pharmacy Leakage (₹50 leakage prevented per OPD ticket with audit controls)
    const opdSavings = opdVolume * 50;
    // 3. TPA claims speedup and reduced rejection costs (Flat ₹5,000 for Professional, ₹15,000 for Enterprise)
    const tpaSavings = tier === 'Starter' ? 1500 : tier === 'Professional' ? 7500 : 18000;

    const totalSavings = bedSavings + opdSavings + tpaSavings;
    const roi = totalSavings / baseCost;

    return {
      tier,
      cost: baseCost,
      savings: totalSavings,
      roi: roi.toFixed(1)
    };
  };

  const { tier: recommendedTier, cost: estimatedCost, savings: savingsAmount, roi: roiRatio } = calculateROI();


  const faqs = [
    {
      question: 'Is there a free trial available?',
      answer: 'Yes. We provide a 30‑day free trial of our HMS Software with full access to core modules so your team can evaluate real workflows end‑to‑end. You can invite multiple users, import sample data, and explore reports without limitations. No credit card is required to start, and you can convert to a paid plan at any time with zero data loss.'
    },
    {
      question: 'Can I upgrade or downgrade my plan?',
      answer: 'Absolutely. Our HMS Software Pricing is flexible and plans can be changed at any time from your admin console. Billing is prorated so you only pay for what you use, and permissions/limits update instantly. We also support add‑on modules (e.g., CGHS billing or analytics) that you can enable or disable per location when needed.'
    },
    {
      question: 'Is my data secure and HIPAA compliant?',
      answer: 'Yes. Our Cloud-Based Hospital Management System follows industry best practices including encryption at rest and in transit, robust role‑based access control, detailed audit logs, and regular third‑party security reviews. The platform is designed to align with HIPAA, SOC 2, and ISO controls so you can meet internal and external compliance requirements with confidence.'
    },
    {
      question: 'Do you provide data migration services?',
      answer: 'Yes. Our implementation team assists with secure data extraction, validation, and import from legacy systems or spreadsheets. We offer mapping templates for patients, encounters, charges, and catalogs, and run reconciliations so the cut‑over is smooth with minimal downtime.'
    },
    {
      question: 'What kind of support do you offer?',
      answer: 'We provide 24/7 multi‑channel support via email, phone, and live chat with defined SLAs. Enterprise customers get a dedicated customer success manager, quarterly business reviews, and priority incident handling. Our knowledge base and in‑app guides help new staff become productive quickly.'
    },
    // India-focused FAQs (SEO)
    {
      question: 'Is Mediway Nextgen compliant with NABH standards?',
      answer: 'Yes. Mediway Nextgen includes NABH‑aligned workflows such as policy/document control, consents, audit trails, and corrective action tracking. Billing and clinical modules generate the reports typically requested during accreditation and surveillance audits, helping your team stay continuously compliant.'
    },
    {
      question: 'Does Mediway Nextgen support CGHS and TPA workflows?',
      answer: 'Yes. We support CGHS package catalogs, pre‑authorization, e‑claims, and re‑submission with denial reasons captured for analytics. For private TPAs, you can configure payor rules, track SLAs, and receive alerts on pending approvals so cash‑flows are predictable and faster.'
    },
    {
      question: 'Is Mediway Nextgen suitable for Indian public and private hospitals?',
      answer: 'Absolutely. The platform is used by clinics and multi‑specialty hospitals alike, with configurations for government empanelments (CGHS, Railway) and private insurance. Multi‑location support, tariff groups, and robust permissions make it practical for both public and private providers across India.'
    },
    {
      question: 'Does Mediway Nextgen support GST and Indian regulatory requirements?',
      answer: 'Yes. We generate GST‑compliant invoices with HSN/SAC codes where applicable, support ICD‑10 coding, and integrate via HL7/FHIR for interoperability. Financial reports align to the Indian financial year, and exports are available for accounting systems your finance team already uses.'
    },
    {
      question: 'Does it support Ayushman Bharat (AB-PMJAY)?',
      answer: 'Yes. You can maintain AB‑PMJAY package catalogs, pre‑auth workflows, and claims exports with complete tracking of statuses and denials. The built‑in analytics help you identify bottlenecks by specialty, package, or payor so reimbursement cycles improve over time.'
    },
    {
      question: 'Is onboarding and training available?',
      answer: 'Yes. We provide a structured onboarding plan with role‑based training for registration, billing, nursing, and admin teams. Sandbox environments and guided checklists ensure staff are ready before go‑live, and our success team remains engaged post‑launch to optimize adoption.'
    }
  ];

  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elementId = entry.target.dataset.element;
            setVisibleElements(prev => [...prev, elementId]);
          }
        });
      },
      { threshold: 0.2 }
    );

    const elements = sectionRef.current?.querySelectorAll('[data-element]');
    elements?.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, []);

  const getPrice = (plan) => {
    return billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
  };

  // Removed unused getPriceLabel to satisfy linter

  return (
    <section id="pricing" className="pricing" ref={sectionRef}>
      <Helmet>
        <title>Pricing — Mediway Nextgen HMS</title>
        <meta name="description" content="Transparent pricing for Mediway Nextgen hospital management software. Plans for clinics, hospitals, and enterprise with 30-day free trial." />
        <link rel="canonical" href="https://mediwaynextgen.wayug.com/#pricing" />
      </Helmet>
      <div className="container">
        {/* Urgency Banner */}
        <div className="pricing-urgency-banner">
          🎉 <strong>Limited Time Offer:</strong> First 3 months at <strong>50% off</strong> for new hospitals. Offer ends May 31, 2026.
        </div>

        {/* FAQ JSON-LD for SEO */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faqs.map(f => ({
                '@type': 'Question',
                name: f.question,
                acceptedAnswer: { '@type': 'Answer', text: f.answer }
              }))
            })
          }}
        />
        <div 
          className={`section-header ${visibleElements.includes('header') ? 'section-header--visible' : ''}`}
          data-element="header"
        >
          <h2 className="section-title">
            Affordable Hospital Software Price India Setup
          </h2>
          <p className="section-subtitle">
            Compare packages for the best HMS India 2025 has to offer. Choose transparent pricing options tailored to your bed count and OPD volume with zero hidden fees.
          </p>
        </div>

        {/* Billing Cycle Toggle */}
        <div 
          className={`billing-toggle ${visibleElements.includes('toggle') ? 'billing-toggle--visible' : ''}`}
          data-element="toggle"
        >
          <div className="toggle-container">
            <span className={`toggle-label ${billingCycle === 'monthly' ? 'toggle-label--active' : ''}`}>
              Monthly
            </span>
            <div className="toggle-switch">
              <input
                type="checkbox"
                id="billing-toggle"
                checked={billingCycle === 'yearly'}
                onChange={(e) => setBillingCycle(e.target.checked ? 'yearly' : 'monthly')}
              />
              <label htmlFor="billing-toggle" className="toggle-slider"></label>
            </div>
            <span className={`toggle-label ${billingCycle === 'yearly' ? 'toggle-label--active' : ''}`}>
              Yearly
              <span className="savings-badge">Save 33%</span>
            </span>
          </div>
        </div>

        {/* Pricing Plans */}
        <div 
          className={`pricing-grid ${visibleElements.includes('plans') ? 'pricing-grid--visible' : ''}`}
          data-element="plans"
        >
          {plans.map((plan, index) => (
            <div
              key={plan.id}
              className={`pricing-card ${plan.popular ? 'pricing-card--popular' : ''}`}
            >
              {plan.popular && <div className="popular-badge">Most Popular</div>}

              <div className="pricing-card__header">
                <div className="plan-icon">
                  <span>{plan.icon}</span>
                </div>
                <h3 className="plan-name">{plan.name}</h3>
                <p className="plan-description">{plan.description}</p>
              </div>

              <div className="pricing-card__price">
                <div className="price-container">
                  <span className="currency">₹</span>
                  <span className="price">{plan.monthlyPrice.toLocaleString('en-IN')}</span>
                  <span className="period">/{billingCycle === 'monthly' ? 'month' : 'year'}</span>
                </div>
                {billingCycle === 'yearly' && (
                  <div className="savings">
                    Save ₹{(plan.monthlyPrice * 12 - plan.yearlyPrice).toLocaleString('en-IN')} annually
                  </div>
                )}
              </div>

              <div className="pricing-card__features">
                <h4>Everything included:</h4>
                <ul className="features-list">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="feature-item">
                      <div className="feature-check">✓</div>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="plan-limits">
                  <h5>Plan limits:</h5>
                  {plan.limits.map((limit, idx) => (
                    <div key={idx} className="limit-item">{limit}</div>
                  ))}
                </div>
              </div>

              <div className="pricing-card__action">
                <button className={`btn ${plan.popular ? 'btn-primary' : 'btn-secondary'} plan-button`}>
                  {plan.buttonText}
                </button>
                <p className="trial-note">30-day free trial • No credit card required</p>
              </div>
            </div>
          ))}
        </div>

        {/* Interactive B2B Cost & ROI Calculator */}
        <div className="roi-calculator">
          <h3>Hospital Software Price India &amp; ROI Estimator</h3>
          <p className="roi-subtitle">Select your facility size to estimate your custom hospital software price in India and compute your dynamic monthly ROI.</p>
          
          <div className="calculator-grid">
            <div className="calculator-inputs">
              <div className="input-group">
                <label>Number of Hospital Beds: <strong>{beds} Beds</strong></label>
                <input 
                  type="range" 
                  min="5" 
                  max="150" 
                  value={beds} 
                  onChange={(e) => setBeds(parseInt(e.target.value))} 
                  className="calculator-slider"
                />
                <div className="slider-labels">
                  <span>5 Beds</span>
                  <span>75 Beds</span>
                  <span>150+ Beds</span>
                </div>
              </div>

              <div className="input-group">
                <label>Monthly OPD Consultations: <strong>{opdVolume.toLocaleString('en-IN')} Patients</strong></label>
                <input 
                  type="range" 
                  min="100" 
                  max="5000" 
                  step="100"
                  value={opdVolume} 
                  onChange={(e) => setOpdVolume(parseInt(e.target.value))} 
                  className="calculator-slider"
                />
                <div className="slider-labels">
                  <span>100</span>
                  <span>2,500</span>
                  <span>5,000+</span>
                </div>
              </div>
            </div>

            <div className="calculator-results">
              <div className="recommended-badge">Recommended Plan: <strong>{recommendedTier}</strong></div>
              <div className="result-metric">
                <span className="metric-label">Estimated HMS Cost:</span>
                <span className="metric-value">₹{estimatedCost.toLocaleString('en-IN')}<small>/month</small></span>
              </div>
              <div className="result-metric highlight">
                <span className="metric-label">Estimated Revenue Reclaimed:</span>
                <span className="metric-value">₹{savingsAmount.toLocaleString('en-IN')}<small>/month</small></span>
              </div>
              <div className="roi-verdict">
                Estimated ROI: <strong>{roiRatio}x</strong> return on software cost.
              </div>
              <div className="calculator-cta">
                <Link to="/contact" className="btn btn-primary">Lock-in Special B2B Quote</Link>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div 
          className={`pricing-faq ${visibleElements.includes('faq') ? 'pricing-faq--visible' : ''}`}
          data-element="faq"
        >
          <h3>Frequently Asked Questions</h3>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`faq-item ${openFaq === index ? 'faq-item--open' : ''}`}
              >
                <button
                  className="faq-question"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span>{faq.question}</span>
                  <div className="faq-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>
                <div className="faq-answer">
                  <p>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Enterprise CTA */}
        <div 
          className={`enterprise-cta ${visibleElements.includes('cta') ? 'enterprise-cta--visible' : ''}`}
          data-element="cta"
        >
          <div className="cta-content">
            <h3>Need a Custom Solution for Your Hospital Chain?</h3>
            <p>Our Enterprise team creates tailored HMS solutions for large hospital networks, government hospitals, and healthcare groups across India.</p>
            <Link to="/contact" className="btn btn-primary">
              Talk to Enterprise Sales
            </Link>
          </div>
        </div>
      </div>

      <div className="pricing-background">
        <div className="background-shapes">
          <div className="shape shape--1"></div>
          <div className="shape shape--2"></div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;